/**
 * Razorpay Checkout.js loader + opener — the frontend half of the real
 * (non-mocked) online payment flow. Backend half lives in
 * ``razorpay_gateway.py`` / ``POST /payments/razorpay/create-order`` /
 * the payment-verification branch of ``POST /orders/checkout``.
 *
 * Flow: caller already has a server-created Razorpay order (from
 * ``POST /payments/razorpay/create-order``) — this just opens the
 * Checkout.js payment sheet against it and resolves with the payment
 * proof on success, or rejects (dismissed / failed) otherwise. The
 * caller is responsible for forwarding the proof to
 * ``POST /orders/checkout`` — this module never talks to our backend
 * directly, only to Razorpay's own script.
 */

let _scriptPromise = null;

/** Whether Checkout.js needs ``webview_intent: true`` — Razorpay-
 * documented flag required for UPI-intent apps (PhonePe, Google Pay)
 * to work when Checkout.js is embedded in an Android WebView instead
 * of a full Chrome tab. Without it, Razorpay's own JS assumes the
 * embedding context can't handle an ``intent://`` redirect and
 * silently falls back to a different (non-intent) UPI flow for those
 * two apps specifically — matching the exact bug reported (PhonePe /
 * GPay failing, other UPI paths still working).
 *
 * Scoped to native Android only (never the website, never iOS — this
 * flag is Android-WebView-specific per Razorpay's docs) so the
 * existing website checkout flow is byte-for-byte unchanged. */
async function needsWebviewIntent() {
    try {
        const { Capacitor } = await import("@capacitor/core");
        return !!Capacitor?.isNativePlatform?.() && Capacitor.getPlatform() === "android";
    } catch {
        return false;
    }
}

/* NOTE — there is deliberately no iOS status-bar handling in this file.
 *
 * Razorpay's checkout renders inside a cross-origin
 * ``iframe.razorpay-checkout-frame``, so its header (back button,
 * merchant logo, title) is drawn by Razorpay's own document and is
 * unreachable from our CSS or JS; all we could ever move was the outer
 * box, against Razorpay's own runtime-injected ``!important`` rules.
 *
 * It's handled one layer down instead, where nothing can override it:
 * ``BizilSafeAreaBridgeViewController`` (generated into the iOS project
 * by ``scripts/_build_common.py:patch_ios_safe_area``) pins the
 * WKWebView's top edge to the native safe-area layout guide, so the web
 * viewport begins below the status bar and Razorpay's iframe lands in
 * the safe area on its own. Don't re-add DOM-level workarounds here. */

function loadCheckoutScript() {
    if (typeof window === "undefined") return Promise.reject(new Error("no window"));
    if (window.Razorpay) return Promise.resolve();
    if (_scriptPromise) return _scriptPromise;
    _scriptPromise = new Promise((resolve, reject) => {
        const el = document.createElement("script");
        el.src = "https://checkout.razorpay.com/v1/checkout.js";
        el.async = true;
        el.onload = () => resolve();
        el.onerror = () => reject(new Error("Could not load Razorpay checkout script"));
        document.body.appendChild(el);
    });
    return _scriptPromise;
}

/**
 * Opens the Razorpay Checkout modal for a server-created order.
 *
 * @param {object} opts
 * @param {string} opts.keyId - Razorpay public key id (safe to expose).
 * @param {string} opts.orderId - Razorpay order id from create-order.
 * @param {number} opts.amount - Amount in paise (echoed from create-order).
 * @param {string} [opts.name] - Merchant/brand name shown in the sheet.
 * @param {string} [opts.description] - Short line under the brand name.
 * @param {string} [opts.themeColor] - Hex accent color.
 * @param {{name?, email?, contact?}} [opts.prefill]
 * @returns {Promise<{razorpay_order_id, razorpay_payment_id, razorpay_signature}>}
 */
export async function openRazorpayCheckout({
    keyId, orderId, amount, name, description, themeColor, prefill,
}) {
    const [, webviewIntent] = await Promise.all([
        loadCheckoutScript(), needsWebviewIntent(),
    ]);
    return new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
            key: keyId,
            order_id: orderId,
            amount,
            currency: "INR",
            name: name || "Bizil",
            description: description || "Order payment",
            theme: themeColor ? { color: themeColor } : undefined,
            prefill: prefill || {},
            // Android-app-only — see needsWebviewIntent()'s docstring.
            ...(webviewIntent ? { webview_intent: true } : {}),
            handler: (response) => {
                resolve({
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                });
            },
            modal: {
                // Fires when the customer closes the sheet without paying —
                // without this the caller's promise would hang forever.
                ondismiss: () => { reject(new Error("Payment cancelled")); },
            },
        });
        rzp.on("payment.failed", (resp) => {
            reject(new Error(resp?.error?.description || "Payment failed"));
        });
        rzp.open();
    });
}
